import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { it, enGB, fr, de, es } from "date-fns/locale";
import {
  ArrowLeft, ArrowRight, Check, CalendarIcon, Users, Minus, Plus,
  Mail, Phone, User, MessageSquare, Sparkles, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useT } from "@/i18n/LanguageContext";
import { useBlockedDates, isDateBlocked } from "@/hooks/useBlockedDates";
import { APARTMENT_SEED } from "@/data/apartment";

const LOCALES = { it, en: enGB, fr, de, es };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS_KEYS = ["bk.step1", "bk.step2", "bk.step3"];

function nightsBetween(_a, _b) { return 0; /* unused helper kept for compat */ }
void nightsBetween;

function readStoredWidget() {
  try {
    const stored = sessionStorage.getItem("booking-widget");
    if (!stored) return null;
    const p = JSON.parse(stored);
    return {
      checkIn: p.checkIn ? new Date(p.checkIn) : null,
      checkOut: p.checkOut ? new Date(p.checkOut) : null,
      guests: p.guests || null,
    };
  } catch (_e) {
    return null;
  }
}

export default function Booking() {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const locale = LOCALES[lang] || it;
  const blocked = useBlockedDates();
  const [step, setStep] = useState(1);
  const [apt] = useState(APARTMENT_SEED);
  const stored = readStoredWidget();
  const [checkIn, setCheckIn] = useState(stored?.checkIn || null);
  const [checkOut, setCheckOut] = useState(stored?.checkOut || null);
  const [guests, setGuests] = useState(stored?.guests || { adulti: 2, bambini: 0, neonati: 0 });
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", note: "" });
  const [bookingId, setBookingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [openIn, setOpenIn] = useState(false);
  const [openOut, setOpenOut] = useState(false);
  const [quote, setQuote] = useState({ nights: 0, subtotal: 0, discount: 0, discount_percent: 0, total: 0, average_nightly: 0 });

  const firstBlockedAfter = (start) => {
    if (!start) return null;
    const cur = new Date(start);
    cur.setDate(cur.getDate() + 1);
    for (let i = 0; i < 365; i++) {
      if (isDateBlocked(cur, blocked)) return new Date(cur);
      cur.setDate(cur.getDate() + 1);
    }
    return null;
  };

  // Fetch live quote from backend whenever dates change
  useEffect(() => {
    if (!checkIn || !checkOut || checkOut <= checkIn) {
      // eslint-disable-next-line
      setQuote({ nights: 0, subtotal: 0, discount: 0, discount_percent: 0, total: 0, average_nightly: 0 });
      return;
    }
    let active = true;
    axios.post(`${API}/quote`, {
      check_in: format(checkIn, "yyyy-MM-dd"),
      check_out: format(checkOut, "yyyy-MM-dd"),
    }).then((r) => { if (active) setQuote(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [checkIn, checkOut]);

  const nights = quote.nights;
  const subtotal = quote.subtotal;
  const discount = quote.discount;
  const discountPercent = quote.discount_percent;
  const totalPrice = quote.total;
  const averageNightly = quote.average_nightly;
  const totalGuests = guests.adulti + guests.bambini;

  const updateGuest = (key, delta) => {
    setGuests((g) => {
      const next = { ...g, [key]: Math.max(0, g[key] + delta) };
      if (key === "adulti") next.adulti = Math.max(1, next.adulti);
      return next;
    });
  };

  const canProceedStep1 = checkIn && checkOut && nights > 0;
  const canProceedStep3 = form.nome && form.cognome && form.email && form.telefono;

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const payload = {
        apartment_id: apt.id,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        guests,
        ...form,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      setBookingId(data.id);
      setStep(4);
      toast.success(t("bk.success"));
    } catch (e) {
      toast.error(t("bk.error"));
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const proceedToPayment = async () => {
    if (!bookingId) return;
    setPayLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Impossibile aprire il pagamento");
      }
    } catch (e) {
      toast.error("Errore durante l'avvio del pagamento");
      console.error(e);
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 lg:px-12 pt-28 md:pt-32 pb-24">
        {/* Header */}
        <button
          onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
          data-testid="booking-back"
          className="text-sm text-slate-600 hover:text-navy-700 inline-flex items-center gap-2 mb-6"
        >
          <ArrowLeft size={15} /> {t("bk.back")}
        </button>

        <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-3">{t("bk.eyebrow")}</p>
        <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2">
          {step < 4 ? t("bk.title") : t("bk.title_done")}
        </h1>

        {/* Stepper */}
        {step < 4 && (
          <div className="mt-10 flex items-center gap-2 md:gap-4">
            {STEPS_KEYS.map((sk, i) => (
              <div key={sk} className="flex items-center gap-2 md:gap-4 flex-1">
                <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-medium ${step >= i + 1 ? "bg-navy-600 text-white" : "bg-cream-200 text-slate-600"}`}>
                  {step > i + 1 ? <Check size={13} /> : i + 1}
                </div>
                <span className={`text-xs md:text-sm ${step >= i + 1 ? "text-slate-900" : "text-slate-600"} hidden sm:inline`}>{t(sk)}</span>
                {i < 2 && <div className={`flex-1 h-px ${step > i + 1 ? "bg-navy-600" : "bg-cream-200"}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Dates & Guests */}
        {step === 1 && (
          <section className="mt-10 grid md:grid-cols-2 gap-5">
            <Popover open={openIn} onOpenChange={setOpenIn}>
              <PopoverTrigger asChild>
                <button data-testid="step1-checkin" className="text-left bg-white border border-cream-200 rounded-2xl p-5 hover:border-navy-600 transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.checkin")}</p>
                  <p className="mt-2 text-lg font-medium text-slate-900 flex items-center gap-2">
                    <CalendarIcon size={16} /> {checkIn ? format(checkIn, "EEE d MMM yyyy", { locale }) : t("bk.choose_date")}
                  </p>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-cream-200" align="start">
                <Calendar
                  mode="single"
                  selected={checkIn}
                  onSelect={(d) => {
                    if (!d) return;
                    setCheckIn(d);
                    if (checkOut && d >= checkOut) setCheckOut(null);
                    setOpenIn(false);
                    setTimeout(() => setOpenOut(true), 120);
                  }}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || isDateBlocked(d, blocked)}
                  locale={locale}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Popover open={openOut} onOpenChange={setOpenOut}>
              <PopoverTrigger asChild>
                <button data-testid="step1-checkout" className="text-left bg-white border border-cream-200 rounded-2xl p-5 hover:border-navy-600 transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.checkout")}</p>
                  <p className="mt-2 text-lg font-medium text-slate-900 flex items-center gap-2">
                    <CalendarIcon size={16} /> {checkOut ? format(checkOut, "EEE d MMM yyyy", { locale }) : t("bk.choose_date")}
                  </p>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-cream-200" align="start">
                <Calendar
                  mode="single"
                  selected={checkOut}
                  onSelect={(d) => {
                    if (!d) return;
                    setCheckOut(d);
                    setOpenOut(false);
                  }}
                  disabled={(d) => {
                    const today = new Date(new Date().setHours(0, 0, 0, 0));
                    if (d < today) return true;
                    if (checkIn && d <= checkIn) return true;
                    const cap = firstBlockedAfter(checkIn);
                    if (cap && d > cap) return true;
                    return isDateBlocked(d, blocked);
                  }}
                  locale={locale}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <div className="md:col-span-2 bg-white border border-cream-200 rounded-2xl p-6">
              <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold mb-4 flex items-center gap-2"><Users size={13} /> {t("bw.guests")}</p>
              {[
                { key: "adulti", label: t("bw.adults"), hint: t("bw.adults_hint") },
                { key: "bambini", label: t("bw.children"), hint: t("bw.children_hint") },
                { key: "neonati", label: t("bw.infants"), hint: t("bw.infants_hint") },
              ].map((row, i) => (
                <div key={row.key} className={`flex items-center justify-between ${i > 0 ? "mt-4" : ""}`}>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{row.label}</p>
                    <p className="text-xs text-slate-600">{row.hint}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`step1-${row.key}-minus`}
                      onClick={() => updateGuest(row.key, -1)}
                      disabled={(row.key === "adulti" && guests.adulti <= 1) || (row.key !== "adulti" && guests[row.key] <= 0)}
                      className="w-8 h-8 rounded-full border border-cream-200 grid place-items-center disabled:opacity-30 hover:border-slate-900"
                    ><Minus size={14} /></button>
                    <span className="w-5 text-center text-sm font-medium">{guests[row.key]}</span>
                    <button
                      data-testid={`step1-${row.key}-plus`}
                      onClick={() => updateGuest(row.key, 1)}
                      className="w-8 h-8 rounded-full border border-cream-200 grid place-items-center hover:border-slate-900"
                    ><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="md:col-span-2 flex justify-end">
              <button
                data-testid="step1-next"
                disabled={!canProceedStep1}
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 rounded-full bg-navy-600 hover:bg-navy-700 disabled:opacity-50 text-white px-8 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
              >
                Continua <ArrowRight size={15} />
              </button>
            </div>
          </section>
        )}

        {/* Step 2: Availability */}
        {step === 2 && (
          <section className="mt-10">
            <div className="bg-white border border-cream-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-5">
                <Sparkles size={18} className="text-navy-700" />
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{t("bk.available")}</h2>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-cream-50 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.checkin")}</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{checkIn && format(checkIn, "d MMM yyyy", { locale })}</p>
                </div>
                <div className="bg-cream-50 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.checkout")}</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{checkOut && format(checkOut, "d MMM yyyy", { locale })}</p>
                </div>
                <div className="bg-cream-50 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold">{t("bw.guests")}</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{totalGuests} {totalGuests === 1 ? t("bw.guest_one") : t("bw.guest_many")}{guests.neonati ? ` + ${guests.neonati}` : ""}</p>
                </div>
              </div>

              {discountPercent > 0 && (
                <div data-testid="discount-banner" className="mt-6 flex items-start gap-3 rounded-xl bg-navy-50 border border-navy-100 px-4 py-3.5 text-sm text-navy-700">
                  <Tag size={16} className="mt-0.5 shrink-0" />
                  <p className="font-medium leading-snug">{t("bk.discount_banner")}</p>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-cream-200 space-y-2">
                <div className="flex items-center justify-between text-sm text-slate-800">
                  <span>€ {averageNightly} × {nights} {nights === 1 ? t("bk.nights_one") : t("bk.nights_many")} <span className="text-slate-500">({t("bk.avg_nightly")})</span></span>
                  <span>€ {subtotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-800">
                  <span>{t("bk.cleaning")}</span>
                  <span>€ 0,00</span>
                </div>
                {discount > 0 && (
                  <div className="flex items-center justify-between text-sm text-navy-700 font-medium">
                    <span>{t("bk.discount_label")}</span>
                    <span>− € {discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-cream-100 font-bold text-slate-900">
                  <span>{t("bk.total")}</span>
                  <span data-testid="step2-total" className="text-xl">€ {totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                data-testid="step2-next"
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 rounded-full bg-navy-600 hover:bg-navy-700 text-white px-8 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
              >
                {t("bk.enter_details")} <ArrowRight size={15} />
              </button>
            </div>
          </section>
        )}

        {/* Step 3: Guest info */}
        {step === 3 && (
          <section className="mt-10">
            <div className="bg-white border border-cream-200 rounded-2xl p-6 md:p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <Field icon={User} label={t("bk.first_name")}>
                  <Input data-testid="step3-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Marco" />
                </Field>
                <Field icon={User} label={t("bk.last_name")}>
                  <Input data-testid="step3-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} placeholder="Rossi" />
                </Field>
                <Field icon={Mail} label={t("bk.email")}>
                  <Input data-testid="step3-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="marco@email.it" />
                </Field>
                <Field icon={Phone} label={t("bk.phone")}>
                  <Input data-testid="step3-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39 333 1234567" />
                </Field>
              </div>
              <Field icon={MessageSquare} label={t("bk.notes")}>
                <Textarea data-testid="step3-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("bk.notes_ph")} rows={4} />
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                data-testid="step3-submit"
                disabled={!canProceedStep3 || submitting}
                onClick={submitBooking}
                className="inline-flex items-center gap-2 rounded-full bg-navy-600 hover:bg-navy-700 disabled:opacity-50 text-white px-8 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
              >
                {submitting ? t("bk.sending") : t("bk.send_request")} <ArrowRight size={15} />
              </button>
            </div>
          </section>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <section className="mt-10">
            <div className="bg-white border border-cream-200 rounded-2xl p-10 md:p-14 text-center">
              <div className="w-16 h-16 rounded-full bg-navy-50 grid place-items-center mx-auto">
                <Check size={26} className="text-navy-700" />
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 display-tight-2 mt-6 leading-tight">
                {t("bk.success")}
              </h2>
              <p className="text-slate-600 mt-4 max-w-xl mx-auto leading-relaxed">
                {t("bk.success_text")} <strong className="text-slate-900">{form.email}</strong>.
              </p>
              {bookingId && (
                <p className="mt-3 text-xs text-slate-600">
                  {t("bk.req_number")}: <span className="font-mono text-slate-900">{bookingId.slice(0, 8).toUpperCase()}</span>
                </p>
              )}

              <div className="mt-10 max-w-md mx-auto bg-cream-50 rounded-2xl p-6 text-left">
                <p className="text-xs uppercase tracking-[0.18em] text-navy-700 font-semibold mb-3">{t("bk.confirm_now")}</p>
                <p className="text-sm text-slate-800 leading-relaxed">
                  {t("bk.confirm_text")}
                </p>
                <button
                  data-testid="step4-pay"
                  disabled={payLoading}
                  onClick={proceedToPayment}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-cream-50 px-6 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
                >
                  {payLoading ? t("bk.redirecting") : `${t("bk.pay_now")} € ${totalPrice.toFixed(2)}`}
                </button>
              </div>

              <button
                onClick={() => navigate("/")}
                data-testid="step4-home"
                className="mt-10 text-sm text-slate-600 hover:text-navy-700 transition-colors"
              >
                {t("bk.home")}
              </button>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-navy-700 font-semibold flex items-center gap-2 mb-2">
        <Icon size={12} /> {label}
      </span>
      {children}
    </label>
  );
}
